import type { Category, Neighborhood } from '../../api/schemas';
import { categoryLabel } from '../../admin/lib/category';
import {
  COUNTRY_OPTIONS,
  currentDevice,
  isZonedCountry,
  readCountryChoice,
  WORLD_COUNTRIES,
  zonesFor,
  type CountryCode,
} from '../../lib/country';
import { CATEGORY_HINT, hintFor, INDEPENDENT_TRADE } from './copy';
import { categoriesFor, type SignupMode } from './draft';
import { Named, Question, type StepProps } from './Question';

type TileOption<T extends string> = { value: T; label: string; hint?: string };

type TilesProps<T extends string> = {
  labelledBy: string;
  options: readonly TileOption<T>[];
  selected: T | null;
  onChoose: (value: T) => void;
  compact?: boolean;
};

/** A grid of big, tactile answers: tap one and the wizard moves on. */
function Tiles<T extends string>({ labelledBy, options, selected, onChoose, compact }: TilesProps<T>) {
  return (
    <div className="wiz-tiles" role="radiogroup" aria-labelledby={labelledBy} data-compact={compact ? 'true' : undefined}>
      {options.map((option, index) => {
        const checked = option.value === selected;
        return (
          <button
            key={option.value}
            type="button"
            role="radio"
            aria-checked={checked}
            className="wiz-tile"
            style={{ ['--i' as string]: index }}
            autoFocus={checked}
            onClick={() => onChoose(option.value)}
          >
            <span className="wiz-tile-label">{option.label}</span>
            {option.hint ? <span className="wiz-tile-hint">{option.hint}</span> : null}
            <span className="wiz-tile-check" aria-hidden="true">
              <svg viewBox="0 0 16 16" width="14" height="14">
                <path d="m3.5 8.5 3 3 6-7" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </span>
          </button>
        );
      })}
    </div>
  );
}

/** A business picks its type; an independent picks their trade, in their own words. */
function categoryOptions(mode: SignupMode): readonly TileOption<Category>[] {
  return categoriesFor(mode).map((value) => {
    const trade = mode === 'independiente' ? INDEPENDENT_TRADE[value] : undefined;
    return { value, label: trade?.label ?? categoryLabel(value), hint: trade?.hint ?? CATEGORY_HINT[value] };
  });
}

export function CategoryStep({ draft, next, choose, error, attempt }: StepProps) {
  const name = draft.businessName.trim();
  const title =
    draft.mode === 'independiente' ? (
      '¿A qué te dedicas?'
    ) : (
      <>
        ¿Qué tipo de negocio es <Named>{name}</Named>?
      </>
    );
  return (
    <Question
      id="tipo"
      title={title}
      hint={hintFor(draft.mode, 'tipo')}
      error={error}
      attempt={attempt}
      onSubmit={next}
    >
      <Tiles labelledBy="tipo-title" options={categoryOptions(draft.mode)} selected={draft.category} onChoose={(category) => choose({ category })} />
    </Question>
  );
}

/** The country "Otro país" opens on: the device's own, or the United States when the device is in Panamá or Venezuela. */
function firstOtherCountry(): string {
  const detected = readCountryChoice('', currentDevice());
  return isZonedCountry(detected) ? 'US' : detected;
}

/**
 * Panamá and Venezuela pick a Dale zone; any other country (Martín, 2 Nov 2026) picks itself from the list and the
 * business writes its own city or area.
 */
export function ZoneStep({ draft, update, next, choose, error, attempt }: StepProps) {
  const zoned = isZonedCountry(draft.country);
  const zones: readonly TileOption<Neighborhood>[] = zonesFor(draft.country).map((zone) => ({ value: zone, label: zone }));
  const solo = draft.mode === 'independiente';
  const title = zoned
    ? solo ? '¿En qué zona trabajas?' : '¿En qué zona está?'
    : solo ? '¿En qué ciudad o zona trabajas?' : '¿En qué ciudad o zona está?';
  return (
    <Question id="zona" title={title} hint={hintFor(draft.mode, 'zona')} error={error} attempt={attempt} onSubmit={next}>
      <div className="seg wiz-country" role="group" aria-label="País">
        {COUNTRY_OPTIONS.map((option) => (
          <button
            key={option.value}
            type="button"
            aria-pressed={draft.country === option.value}
            onClick={() => update({ country: option.value as CountryCode })}
          >
            {option.label}
          </button>
        ))}
        <button type="button" aria-pressed={!zoned} onClick={() => (zoned ? update({ country: firstOtherCountry() }) : undefined)}>
          Otro país
        </button>
      </div>
      {zoned ? (
        <Tiles
          labelledBy="zona-title"
          options={zones}
          selected={draft.neighborhood as Neighborhood | null}
          onChoose={(neighborhood) => choose({ neighborhood })}
          compact
        />
      ) : (
        <div className="wiz-abroad">
          <label className="wiz-abroad__country">
            <span>País</span>
            <select className="wiz-select" value={draft.country} onChange={(event) => update({ country: event.target.value })}>
              {WORLD_COUNTRIES.map((country) => (
                <option key={country.value} value={country.value}>
                  {country.label}
                </option>
              ))}
            </select>
          </label>
          <input
            className="wiz-input"
            aria-labelledby="zona-title"
            autoComplete="address-level2"
            placeholder="Por ejemplo, Brickell, Miami"
            value={draft.neighborhood ?? ''}
            onChange={(event) => update({ neighborhood: event.target.value })}
          />
        </div>
      )}
    </Question>
  );
}
